import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'
import { css } from 'styled-components'

import {
  existingInviteCodeAtom,
  type InviteCode,
} from '@root/entities/referral'
import { reatomUseInviteCodeForm } from '@root/features/referral/atoms/reatom-use-invite-code-form'
import { useTranslation } from '@root/shared/localization'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

import { InviteCodeForm } from '../common/invite-code-form'
import { InviteCodeUserInfo } from '../common/invite-code-user-info'

type Props = {
  onDone: () => void
  onCancel: () => void
  initialInviteCode: InviteCode | undefined
}

export const AddInviteCodeDialog = ({
  onDone,
  onCancel,
  initialInviteCode,
}: Props) => {
  const { t } = useTranslation()

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUseInviteCodeForm({ initialInviteCode }),
    [initialInviteCode],
  )
  const [existingInviteCode] = useAtom(existingInviteCodeAtom)

  return (
    <DialogAnnouncement
      css={css`
        --announcement-header-background: var(--appearanceA-7);
        --announcement-body-background: var(--background-1);
      `}
      withSeparator
      header={
        <Header>
          <Title>{t('referral.add-invite-code')}</Title>
          <SubTitle>{t('referral.add-invite-code-description')}</SubTitle>
          <InviteCodeUserInfo inviterStateAtom={model.getInviterState} />
        </Header>
      }
    >
      <Form
        device="desktop"
        existingInviteCode={existingInviteCode}
        model={model}
        onDone={onDone}
        onCancel={onCancel}
      />
    </DialogAnnouncement>
  )
}

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  position: relative;
  text-align: center;
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: normal;
  line-height: 140%;
`
const Form = styled(InviteCodeForm)`
  padding: 20px 12px;
`

const SubTitle = styled.h3`
  color: var(--textA-10);
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  margin-top: 2px;

  strong {
    color: var(--textA-11);
  }
`
